import { WithClassname } from '../../../../types';
import useTips, { TipOption } from '../../../../hooks/order/useTips';
import { useEffect, useMemo, useRef, useState } from 'react';
import useOngoingOrder from '../../../../hooks/context/useOngoingOrder';
import useOrderBasket from '../../../../hooks/context/useOrderBasket';
import { Collection } from 'collect.js';
import React from 'react';
import { InputNumber } from 'antd';
import { CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import useHelpers from '../../../../hooks/utility/useHelpers';
import classnames from 'classnames';
import useStore from '../../../../hooks/context/useStore';

type Item = TipOption & {
  is_selected: boolean;
  amount: number;
};

export default function Tips({ className }: WithClassname) {
  const { currency } = useStore().storeData;
  const { orderData, updateOrderData } = useOngoingOrder();
  const { subtotal } = useOrderBasket();
  const { options, calculate } = useTips();
  const { formatCurrency } = useHelpers();

  const [activeIndex, setActiveIndex] = useState(getDefaultActiveIndex());
  const [customTipAmount, setCustomTipAmount] = useState<number>(orderData.tip);

  const customTipInputRef = useRef(null);

  const items = useMemo<Collection<Item>>(
    () =>
      options.map((item) => ({
        ...item,
        is_selected: activeIndex === item.index,
        amount: item.is_custom ? customTipAmount : calculate(subtotal, item),
      })),
    [options, activeIndex, subtotal, customTipAmount],
  );

  const predefinedItems = useMemo<Item[]>(
    () => items.filter((item: Item) => !item.is_zero && !item.is_custom).toArray(),
    [items],
  );

  const zeroOption = items.first();
  const customOption = items.firstWhere('is_custom');
  const selectedOption = items.firstWhere('is_selected');

  useEffect(() => {
    onSelect(selectedOption);
  }, [subtotal]);

  function getDefaultActiveIndex() {
    if (orderData.tip === 0 || !orderData.tip) {
      return options.firstWhere('is_zero', true).index;
    }

    return options.first(
      (item) => calculate(subtotal, item) === orderData.tip,
      options.firstWhere('is_custom', true),
    ).index;
  }

  const onSelect = (option: Item) => {
    setActiveIndex(option.index);
    updateOrderData('tip', option.is_custom ? customTipAmount : calculate(subtotal, option));

    if (option.is_custom) {
      setTimeout(() => customTipInputRef.current.focus(), 5);
    }
  };

  const onChangeCustomTip = (value: number) => {
    setCustomTipAmount(value);
    updateOrderData('tip', value);
  };

  return (
    <div className={className}>
      <div className="w-full grid grid-cols-4 gap-xs flex-nowrap">
        {predefinedItems.map((item: Item) => {
          return (
            <label
              htmlFor={`tip-option-${item.index}`}
              className={itemContainer}
              key={`tip-option-${item.index}`}
            >
              <div className="flex justify-between">
                <p className="my-auto text-sm font-medium">{item.percentage}%</p>
                {item.is_selected && (
                  <CheckCircleOutlined className="h-4 w-4 my-auto ml-2xs text-brand-primary" />
                )}
              </div>
              <p className="text-xs">{formatCurrency(item.amount)}</p>
              <input
                type="radio"
                checked={item.is_selected}
                onChange={(e) => e.target.checked && onSelect(item)}
                className="hidden"
                id={`tip-option-${item.index}`}
              />
            </label>
          );
        })}
        <label htmlFor={`tip-option-${customOption.index}`} className={itemContainer}>
          <div className="w-full h-full flex justify-center items-center">
            <EditOutlined />
            <input
              type="radio"
              checked={customOption.is_selected}
              onChange={(e) => e.target.checked && onSelect(customOption)}
              id={`tip-option-${customOption.index}`}
              className="hidden"
            />
          </div>
        </label>
      </div>
      <div
        className={classnames('mt-xs transition-all duration-300 ease-in-out', {
          hidden: !customOption.is_selected,
        })}
      >
        <InputNumber
          ref={customTipInputRef}
          value={customTipAmount}
          onChange={onChangeCustomTip}
          addonBefore={currency}
          controls={false}
          className="w-full"
          parser={(value) => value?.replace(',', '.') as unknown as number}
        />
      </div>
      <label htmlFor={`tip-option-${zeroOption.index}`} className={noTipButton}>
        <p className="text-sm">No tip</p>
        <input
          type="radio"
          checked={zeroOption.is_selected}
          onChange={(e) => e.target.checked && onSelect(zeroOption)}
          id={`tip-option-${zeroOption.index}`}
          className="hidden"
        />
      </label>
    </div>
  );
}

const itemContainer =
  'py-2xs px-sm flex flex-col justify-center items-center rounded-lg cursor-pointer transition-colors ease-in-out duration-200 border has-[:checked]:border-brand-primary';
const noTipButton =
  'mt-xs py-xs w-full rounded-lg flex justify-center items-center cursor-pointer transition-colors ease-in-out duration-200 border has-[:checked]:border-brand-primary';
